import React, { useState, useEffect } from 'react';
import { 
  History, 
  Download, 
  Eye, 
  Filter,
  Calendar,
  Euro,
  FileText,
  TrendingUp,
  Trash2
} from 'lucide-react';
import { usePayroll } from '@/hooks/usePayroll';
import { generateIndividualPayslip } from '@/utils/generatePayrollPDF';
import { supabase } from '@/lib/supabaseClient';

export const PayrollHistory: React.FC = () => {
  const { payrollHistory, deletePayrollRecord, employees } = usePayroll();
  const [filterMonth, setFilterMonth] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [calculationsData, setCalculationsData] = useState<any>({});

  // Pobierz pełne dane z payroll_calculations
  useEffect(() => {
    const fetchCalculationsData = async () => {
      const recordIds = payrollHistory.map(r => r.id);
      if (recordIds.length === 0) return;

      const { data, error } = await supabase
        .from('payroll_calculations')
        .select('*')
        .in('id', recordIds);

      if (data && !error) {
        const dataMap = data.reduce((acc, calc) => {
          acc[calc.id] = calc;
          return acc;
        }, {});
        setCalculationsData(dataMap);
      }
    };

    fetchCalculationsData();
  }, [payrollHistory]);

  const handleDownloadPDF = (record: any) => {
    // Znajdź pracownika
    const employee = employees.find(e => e.id === record.employeeId);
    if (!employee) {
      alert('Nie znaleziono danych pracownika');
      return;
    }

    // Pobierz pełne dane kalkulacji
    const calculation = calculationsData[record.id];
    if (!calculation) {
      alert('Nie znaleziono danych rozliczenia');
      return;
    }

    // Przygotuj dane do PDF
    const employeeData = {
      firstName: employee.firstName,
      lastName: employee.lastName,
      position: 'Pracownik', // Można dodać to pole do bazy
      email: employee.email
    };

    // Generuj PDF
    try {
      generateIndividualPayslip(employeeData, calculation, record.month);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Błąd podczas generowania PDF');
    }
  };

  const filteredHistory = payrollHistory.filter(record => {
    if (filterMonth && !record.month.startsWith(filterMonth)) return false;
    if (filterEmployee && record.employeeId !== filterEmployee) return false;
    return true;
  });

  const uniqueEmployees = [...new Set(payrollHistory.map(r => r.employeeId))];
  const totalAmount = filteredHistory.reduce((sum, record) => sum + record.grossAmount, 0);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-white">Historia rozliczeń</h2>
        
        {/* Statystyki */}
        <div className="flex gap-4">
          <div className="bg-zinc-800 rounded-lg px-4 py-2 border border-zinc-700">
            <p className="text-xs text-gray-400">Łączna kwota</p>
            <p className="text-lg font-semibold text-green-400">
              {totalAmount.toLocaleString('pl-PL')} zł
            </p>
          </div>
          <div className="bg-zinc-800 rounded-lg px-4 py-2 border border-zinc-700">
            <p className="text-xs text-gray-400">Liczba rozliczeń</p>
            <p className="text-lg font-semibold text-white">{filteredHistory.length}</p>
          </div>
        </div>
      </div>

      {/* Filtry */}
      <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
        <div className="flex items-center gap-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <input
            type="month"
            value={filterMonth}
            onChange={(e) => setFilterMonth(e.target.value)}
            placeholder="Filtruj po miesiącu"
            className="px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
          />
          <select
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            className="px-3 py-2 bg-zinc-700 border border-zinc-600 rounded-lg text-white focus:border-green-500 focus:outline-none"
          >
            <option value="">Wszyscy pracownicy</option>
            {uniqueEmployees.map(id => {
              const emp = employees.find(e => e.id === id);
              return (
                <option key={id} value={id}>
                  {emp ? `${emp.firstName} ${emp.lastName}` : id}
                </option>
              );
            })}
          </select>
          <button
            onClick={() => {
              setFilterMonth('');
              setFilterEmployee('');
            }}
            className="px-3 py-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors"
          >
            Wyczyść filtry
          </button>
        </div>
      </div>

      {/* Lista historii */}
      <div className="space-y-4">
        {filteredHistory.map(record => (
          <div key={record.id} className="bg-zinc-800 rounded-lg p-6 border border-zinc-700">
            <div className="flex items-start justify-between">
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-white">
                    {new Date(record.month + '-01').toLocaleDateString('pl-PL', { 
                      year: 'numeric', 
                      month: 'long' 
                    })}
                  </h3>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Pracownik:</span>
                    <p className="text-white font-medium">{record.employeeName}</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Godziny:</span>
                    <p className="text-white">{record.totalHours}h</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Brutto:</span>
                    <p className="text-white">{record.grossAmount.toLocaleString('pl-PL')} zł</p>
                  </div>
                  <div>
                    <span className="text-gray-400">Netto:</span>
                    <p className="text-green-400 font-medium">
                      {record.netAmount.toLocaleString('pl-PL')} zł
                    </p>
                  </div>
                </div>

                {record.overtime && (record.overtime.hours50 > 0 || record.overtime.hours100 > 0) && (
                  <div className="mt-2 p-3 bg-zinc-700 rounded-lg">
                    <p className="text-xs text-gray-400 mb-1">Nadgodziny:</p>
                    <div className="flex gap-4 text-sm">
                      {record.overtime.hours50 > 0 && (
                        <span className="text-white">50%: {record.overtime.hours50}h</span>
                      )}
                      {record.overtime.hours100 > 0 && (
                        <span className="text-white">100%: {record.overtime.hours100}h</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={() => handleDownloadPDF(record)}
                  className="p-2 bg-zinc-700 text-white rounded-lg hover:bg-zinc-600 transition-colors"
                  title="Pobierz PDF"
                >
                  <Download className="w-5 h-5" />
                </button>
                <button
                  onClick={() => {
                    if (confirm('Czy na pewno chcesz usunąć to rozliczenie?')) {
                      deletePayrollRecord(record.id);
                    }
                  }}
                  className="p-2 bg-zinc-700 text-white rounded-lg hover:bg-red-600 transition-colors"
                  title="Usuń"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredHistory.length === 0 && (
        <div className="text-center py-12">
          <History className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-400 mb-2">Brak historii rozliczeń</h3>
          <p className="text-gray-500">
            {filterMonth || filterEmployee ? 
              'Brak wyników dla wybranych filtrów' : 
              'Historia rozliczeń pojawi się tutaj po pierwszym rozliczeniu'
            }
          </p>
        </div>
      )}
    </div>
  );
};
